import Dynamic from '@/components/modals/dynamic.tsx'
import { popAllModals } from '@/components/modals/index.tsx'
import { Button } from '@/shadcn/components/ui/button'
import { DialogDescription, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog'
import { Loader2 } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ConfirmationModalProps {
  title: string
  subtitle: string
  onConfirm: () => Promise<void> | void
  confirmButtonText?: string
  cancelButtonText?: string
  variant?: 'destructive' | 'indigo' | 'default'
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  title,
  subtitle,
  onConfirm,
  confirmButtonText,
  variant = 'indigo',
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const { t: translate } = useTranslation()

  const handleConfirm = async () => {
    try {
      setIsLoading(true)

      // Check if onConfirm is an async function
      const result = onConfirm()
      if (result instanceof Promise) {
        await result
      }

      // Close modal after successful confirmation
      popAllModals()
    } catch (error) {
      console.error('Error in confirmation action:', error)
      // Optionally, add error handling or notification
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dynamic.Content className="p-4 sm:p-8">
      <DialogHeader className="mb-2">
        <DialogTitle className="mt-6 sm:mt-2">{title}</DialogTitle>
        <DialogDescription>{subtitle}</DialogDescription>
      </DialogHeader>
      <div className="flex justify-end space-x-2 mt-4">
        <Button variant={variant} onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {translate('common.loading')}
            </>
          ) : (
            confirmButtonText
          )}
        </Button>
        <Button variant="secondary" disabled={isLoading} onClick={() => popAllModals()}>
          {translate('common.buttons.cancel')}
        </Button>
      </div>
    </Dynamic.Content>
  )
}

export default ConfirmationModal
